import { useState, useEffect } from 'react';
import { Search, Shield, AlertTriangle, CheckCircle, Clock, Filter, Users, Calendar, FileText, Download, Database, UserCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { AuditLogs } from '@/components/audit/AuditLogs';
import { TwoFactorAuth } from '@/components/security/TwoFactorAuth';
import { WebhookManager } from '@/components/webhooks/WebhookManager';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { AdvancedExport } from '@/components/export/AdvancedExport';
import { CustomizableDashboard } from '@/components/dashboard/CustomizableDashboard';
import BackupManager from '@/components/backup/BackupManager';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { 
  GuardsWidget, 
  TeamsWidget, 
  EmergencyWidget, 
  ScheduleWidget, 
  LeaveWidget, 
  ApplicationsWidget, 
  MissionsWidget, 
  SystemWidget, 
  EquipmentWidget, 
  NotificationsWidget 
} from '@/components/dashboard/widgets';

// Real data hooks
const useSecurityMetrics = () => {
  return useQuery({
    queryKey: ['security-metrics'],
    queryFn: async () => {
      // Get active users count (users who have profiles)
      const { data: activeUsers } = await supabase
        .from('profiles')
        .select('id')
        .not('id', 'is', null);

      // Get emergency reports count in last 24h
      const { data: emergencyReports } = await supabase
        .from('emergency_reports')
        .select('id')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      // Get total users for security score calculation
      const { data: totalUsers } = await supabase
        .from('profiles')
        .select('id');

      // Get last backup info
      const { data: lastBackup } = await supabase
        .from('schedule_backups')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const activeUserCount = activeUsers?.length || 0;
      const emergencyCount = emergencyReports?.length || 0;
      const totalUserCount = totalUsers?.length || 0;
      const securityScore = totalUserCount > 0 ? Math.max(50, 100 - (emergencyCount * 10)) : 100;
      
      const lastBackupTime = lastBackup?.created_at 
        ? Math.floor((Date.now() - new Date(lastBackup.created_at).getTime()) / (1000 * 60))
        : null;

      return [
        {
          title: 'Active Users (24h)',
          value: activeUserCount.toString(),
          status: activeUserCount > 5 ? 'good' : 'normal',
          icon: Users,
          description: 'Users active in last 24 hours'
        },
        {
          title: 'Emergency Reports',
          value: emergencyCount.toString(),
          status: emergencyCount > 5 ? 'error' : emergencyCount > 2 ? 'warning' : 'good',
          icon: AlertTriangle,
          description: 'Emergency reports in last 24h'
        },
        {
          title: 'Security Score',
          value: `${securityScore}%`,
          status: securityScore > 80 ? 'good' : securityScore > 60 ? 'warning' : 'error',
          icon: CheckCircle,
          description: 'Overall system security health'
        },
        {
          title: 'Last Backup',
          value: lastBackupTime ? `${lastBackupTime}m ago` : 'No backups',
          status: !lastBackupTime ? 'error' : lastBackupTime < 60 ? 'good' : 'warning',
          icon: Clock,
          description: 'Most recent data backup'
        }
      ];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Real dashboard widgets with data
const useDashboardWidgets = () => {
  return useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: async () => {
      return [
        {
          id: 'guards-overview',
          type: 'stats',
          title: 'Guards Overview',
          description: 'Guard statistics and status',
          component: GuardsWidget,
          category: 'management' as const,
          size: 'medium' as const
        },
        {
          id: 'teams-overview',
          type: 'stats',
          title: 'Teams Overview',
          description: 'Team performance and metrics',
          component: TeamsWidget,
          category: 'management' as const,
          size: 'medium' as const
        },
        {
          id: 'emergency-reports',
          type: 'alerts',
          title: 'Emergency Reports',
          description: 'Critical alerts and incidents',
          component: EmergencyWidget,
          category: 'alerts' as const,
          size: 'medium' as const
        },
        {
          id: 'schedule-overview',
          type: 'calendar',
          title: 'Schedule Overview',
          description: 'Shifts and scheduling metrics',
          component: ScheduleWidget,
          category: 'analytics' as const,
          size: 'medium' as const
        },
        {
          id: 'leave-requests',
          type: 'list',
          title: 'Leave Requests',
          description: 'Leave management overview',
          component: LeaveWidget,
          category: 'management' as const,
          size: 'medium' as const
        },
        {
          id: 'applications',
          type: 'docs',
          title: 'Applications',
          description: 'Application submissions and status',
          component: ApplicationsWidget,
          category: 'reports' as const,
          size: 'medium' as const
        },
        {
          id: 'missions',
          type: 'tasks',
          title: 'Missions',
          description: 'Mission tracking and progress',
          component: MissionsWidget,
          category: 'analytics' as const,
          size: 'medium' as const
        },
        {
          id: 'system-health',
          type: 'system',
          title: 'System Health',
          description: 'System status and performance',
          component: SystemWidget,
          category: 'analytics' as const,
          size: 'medium' as const
        },
        {
          id: 'equipment',
          type: 'inventory',
          title: 'Equipment',
          description: 'Equipment assignment and tracking',
          component: EquipmentWidget,
          category: 'analytics' as const,
          size: 'medium' as const
        },
        {
          id: 'notifications',
          type: 'alerts',
          title: 'Notifications',
          description: 'System notifications overview',
          component: NotificationsWidget,
          category: 'alerts' as const,
          size: 'medium' as const
        }
      ];
    },
    refetchInterval: 60000,
  });
};

export const EnhancedDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load real data
  const { data: securityMetrics, isLoading: metricsLoading } = useSecurityMetrics();
  const { data: dashboardWidgets, isLoading: widgetsLoading } = useDashboardWidgets();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'search', label: 'Global Search', icon: Search },
    { id: 'dashboard', label: 'Dashboard', icon: CheckCircle },
    { id: 'hr', label: 'HR Management', icon: UserCheck },
    { id: 'backup', label: 'Backup', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'audit', label: 'Audit Logs', icon: AlertTriangle },
    { id: 'webhooks', label: 'Webhooks', icon: Filter },
    { id: 'notifications', label: 'Notifications', icon: Clock },
    { id: 'export', label: 'Export', icon: Download }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-blue-500';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {metricsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Card key={i} className="p-6 animate-pulse">
                    <div className="h-16 bg-muted rounded"></div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {securityMetrics?.map((metric, index) => {
                  const Icon = metric.icon;
                  return (
                    <Card key={index} className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            {metric.title}
                          </p>
                          <p className="text-2xl font-bold">{metric.value}</p>
                          <p className="text-sm text-muted-foreground">
                            {metric.description}
                          </p>
                        </div>
                        <div className="relative">
                          <Icon className="w-8 h-8 text-muted-foreground" />
                          <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${getStatusColor(metric.status)}`} />
                        </div>
                      </div>
                    </Card>
                  );
                }) || []}
              </div>
            )}
          </div>
        );

      case 'search':
        return (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Global Search</h3>
              <p className="text-muted-foreground mb-4">
                Search across all guards, teams, reports, schedules, and more.
              </p>
              <GlobalSearch />
            </Card>
          </div>
        );

      case 'dashboard':
        return (
          <div className="space-y-6">
            {widgetsLoading ? (
              <Card className="p-6 animate-pulse">
                <div className="h-32 bg-muted rounded"></div>
              </Card>
            ) : (
              <CustomizableDashboard
                availableWidgets={dashboardWidgets || []}
                userRoles={['admin']}
              />
            )}
          </div>
        );

      case 'hr':
        return (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">HR Management</h3>
              <p className="text-muted-foreground mb-4">
                Manage candidates, interviews, and HR processes.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-5 h-5 text-primary" />
                    <h4 className="font-medium">Candidates</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    View and manage candidate applications
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => window.location.href = '/hr'}
                    className="w-full"
                  >
                    Manage Candidates
                  </Button>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <h4 className="font-medium">Interviews</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Schedule and manage interviews
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => window.location.href = '/hr/interviews'}
                    className="w-full"
                  >
                    View Interviews
                  </Button>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <h4 className="font-medium">HR Reports</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate HR analytics and reports
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled
                    className="w-full"
                  >
                    Coming Soon
                  </Button>
                </Card>
              </div>
            </Card>
          </div>
        );

      case 'backup':
        return <BackupManager />;

      case 'security':
        return (
          <div className="space-y-6">
            <TwoFactorAuth />
          </div>
        );

      case 'audit':
        return <AuditLogs />;

      case 'webhooks':
        return <WebhookManager />;

      case 'notifications':
        return <NotificationCenter />;

      case 'export':
        return (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Advanced Export</h3>
              <p className="text-muted-foreground mb-4">
                Export your data in various formats with advanced filtering options.
              </p>
              <AdvancedExport />
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Enhanced Dashboard</h2>
          <p className="text-muted-foreground">
            Advanced features and system management
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {renderTabContent()}
      </div>
    </div>
  );
};